import { useState } from 'react';
import type { InvoiceLineInput, InvoiceLineRow } from '../../shared/types';
import { lineExCents, linesSubtotalExCents, validateInvoiceLines } from '../../shared/invoiceLines';
import { grossFromEx, gstOnEx } from '../../shared/finance';
import { formatMoneyExact, parseMoney } from '../lib/moneyFormat';
import { api } from '../lib/api';
import { Modal } from './primitives';

export interface EditableInvoiceLine { description: string; quantity: string; unit: string }
export const editableLines = (lines: readonly InvoiceLineInput[]): EditableInvoiceLine[] => lines.map(line => ({
  description: line.description, quantity: String(line.quantity_milli / 1000), unit: (line.unit_ex_cents / 100).toFixed(2),
}));
export function lineInputs(lines: readonly EditableInvoiceLine[]): InvoiceLineInput[] {
  return lines.map(line => ({ description: line.description,
    // Accept up to three decimal places; do not silently round more precise quantities.
    quantity_milli: /^(?:\d+(?:\.\d{0,3})?|\.\d{1,3})$/.test(line.quantity.trim()) ? Math.round(Number(line.quantity) * 1000) : NaN,
    unit_ex_cents: parseMoney(line.unit) ?? NaN,
  }));
}

export function InvoiceLineTotals({ lines }: { lines: InvoiceLineInput[] }) {
  const subtotal = linesSubtotalExCents(lines);
  const money = (cents: number) => Number.isFinite(cents) ? formatMoneyExact(cents) : '—';
  return <dl className="inv-totals" aria-live="polite">
    <div><dt>Subtotal ex GST</dt><dd>{money(subtotal)}</dd></div>
    <div><dt>GST · 10%</dt><dd>{money(gstOnEx(subtotal))}</dd></div>
    <div className="total"><dt>Total inc GST</dt><dd>{money(grossFromEx(subtotal))}</dd></div>
  </dl>;
}

export function InvoiceLinesEditor({ lines, onChange }: { lines: EditableInvoiceLine[]; onChange: (lines: EditableInvoiceLine[]) => void }) {
  const update = (index: number, patch: Partial<EditableInvoiceLine>) => onChange(lines.map((line, i) => i === index ? { ...line, ...patch } : line));
  return <div className="invoice-lines-editor">
    <div className="f-label-row"><span className="f-label">Lines</span><span className="f-help">Optional · prices ex GST</span></div>
    {lines.length ? <table className="inv-lines editable"><thead><tr>
      <th>Description</th><th>Qty</th><th>Unit ex GST</th><th>Total ex GST</th><th><span className="sr-only">Remove</span></th>
    </tr></thead><tbody>{lines.map((line, index) => {
      const total = lineExCents(lineInputs([line])[0]);
      return <tr key={index}>
        <td data-label="Description"><input className="f-input" aria-label={`Line ${index + 1} description`} maxLength={200} value={line.description} onChange={e => update(index, { description: e.target.value })} /></td>
        <td data-label="Qty"><input className="f-input mono" aria-label={`Line ${index + 1} quantity`} inputMode="decimal" value={line.quantity} onChange={e => update(index, { quantity: e.target.value })} /></td>
        <td data-label="Unit ex GST"><input className="f-input money" aria-label={`Line ${index + 1} unit price ex GST`} inputMode="decimal" value={line.unit} onChange={e => update(index, { unit: e.target.value })} /></td>
        <td data-label="Total ex GST">{Number.isFinite(total) ? formatMoneyExact(total) : '—'}</td>
        <td><button type="button" className="btn inv-remove" aria-label={`Remove line ${index + 1}`} onClick={() => onChange(lines.filter((_, i) => i !== index))}>×</button></td>
      </tr>;
    })}</tbody></table> : <p className="f-help">No lines added. Use a single amount below.</p>}
    <button type="button" className="btn inv-add" disabled={lines.length >= 50} onClick={() => onChange([...lines, { description: '', quantity: '1', unit: '' }])}>+ Add line</button>
  </div>;
}

export function InvoiceLinesTable({ lines }: { lines: InvoiceLineRow[] }) {
  if (!lines.length) return null;
  return <table className="inv-lines"><thead><tr><th>Description</th><th>Qty</th><th>Unit ex GST</th><th>Total ex GST</th></tr></thead>
    <tbody>{lines.map(line => <tr key={line.id}>
      <td data-label="Description">{line.description}</td><td data-label="Qty">{line.quantity_milli / 1000}</td>
      <td data-label="Unit ex GST">{formatMoneyExact(line.unit_ex_cents)}</td><td data-label="Total ex GST">{formatMoneyExact(line.line_ex_cents)}</td>
    </tr>)}</tbody></table>;
}

/** Same editor and save path on desktop and phone; cancel never mutates the invoice. */
export function InvoiceDetailLines({ invoice, onSaved }: { invoice: { id: string; description: string | null; lines?: InvoiceLineRow[]; amount_cents: number; gst_cents: number }; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<EditableInvoiceLine[]>([]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [gstFree, setGstFree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputs = lineInputs(lines);
  const open = () => {
    setLines(editableLines(invoice.lines ?? [])); setDescription(invoice.description ?? '');
    setAmount(((invoice.amount_cents - invoice.gst_cents) / 100).toFixed(2));
    setGstFree(invoice.gst_cents === 0); setError(null); setEditing(true);
  };
  const save = async () => {
    if (busy) return;
    const invalid = validateInvoiceLines(inputs);
    const ex = lines.length ? linesSubtotalExCents(inputs) : parseMoney(amount);
    if (invalid || ex === null) { setError(invalid ?? 'Enter an amount.'); return; }
    setBusy(true); setError(null);
    try {
      await api(`/api/admin/finance/invoices/${invoice.id}`, { method: 'PATCH', json: {
        lines: inputs, description: description.trim() || null,
        ...(!lines.length ? { amount_cents: gstFree ? ex : grossFromEx(ex),
          gst_cents: gstFree ? 0 : gstOnEx(ex) } : {}),
      } });
      setEditing(false); onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save invoice lines.'); }
    finally { setBusy(false); }
  };
  return <section className="invoice-detail-lines">
    <InvoiceLinesTable lines={invoice.lines ?? []} />
    <button type="button" className="btn" onClick={open}>{invoice.lines?.length ? 'Edit lines' : '+ Add lines'}</button>
    {editing && <Modal title="Edit invoice" width={720} onClose={() => { if (!busy) setEditing(false); }} foot={<>
      <button className="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
      <button className="btn primary" disabled={busy} onClick={() => void save()}>Save invoice</button>
    </>}><div className="form-grid inv-line-form">
      <label className="f-field span-2"><span className="f-label">Description · invoice summary</span><input className="f-input" aria-label="Invoice summary" value={description} onChange={e => setDescription(e.target.value)} /></label>
      <div className="f-field span-2"><InvoiceLinesEditor lines={lines} onChange={setLines} /></div>
      {lines.length ? <div className="f-field span-2"><InvoiceLineTotals lines={inputs} /></div> : <>
        <label className="f-field"><span className="f-label">Amount · ex GST</span><input className="f-input money" aria-label="Amount ex GST" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} /></label>
        <label className="f-field"><span className="f-label">GST</span><select className="f-input" aria-label="GST" value={gstFree ? 'free' : 'tax'} onChange={e => setGstFree(e.target.value === 'free')}><option value="tax">+ 10% on top</option><option value="free">GST free</option></select></label>
      </>}
      {error && <p className="error-text span-2" role="alert">{error}</p>}
    </div></Modal>}
  </section>;
}
